import React, { useEffect, useState } from "react";
import axios from "axios";
import { getAccessToken } from "../utils/auth";
import { API_BASE_URL } from "../utils/api";
import { compareSwimmerNamesByLastName } from "../utils/swimmerSort";

const API = API_BASE_URL;

const TradingView = ({ user }) => {
  const [stateData, setStateData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("");

  const [toCoach, setToCoach] = useState("");
  const [offeredSwimmer, setOfferedSwimmer] = useState("");
  const [requestedSwimmer, setRequestedSwimmer] = useState("");
  const [tradeNote, setTradeNote] = useState("");

  const fetchState = async () => {
    const token = getAccessToken();
    if (!token) {
      setLoading(false);
      setStatus("You must be logged in to use trading.");
      return;
    }

    try {
      const res = await axios.get(`${API}/trades/state`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStateData(res.data);
      setStatus("");
    } catch (error) {
      setStatus(
        error?.response?.data?.detail || "Unable to load trading data.",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchState();
  }, []);

  const coaches = stateData?.coaches || [];
  const coachTeams = stateData?.coach_teams || {};
  const myTeam = stateData?.my_team || [];
  const proposals = stateData?.trade_proposals || [];

  const targetTeam = toCoach ? coachTeams[toCoach] || [] : [];
  const sortedMyTeam = [...myTeam].sort((a, b) =>
    compareSwimmerNamesByLastName(a, b),
  );
  const sortedTargetTeam = [...targetTeam].sort((a, b) =>
    compareSwimmerNamesByLastName(a, b),
  );

  const incomingProposals = proposals.filter(
    (proposal) => proposal.to_coach === stateData?.my_coach,
  );

  const outgoingProposals = proposals.filter(
    (proposal) => proposal.from_coach === stateData?.my_coach,
  );

  const adminProposalQueue = proposals.filter(
    (proposal) => proposal.status === "pending_admin",
  );

  const submitProposal = async () => {
    if (!toCoach || !offeredSwimmer || !requestedSwimmer) {
      setStatus("Select coach, offered swimmer, and requested swimmer.");
      return;
    }
    try {
      await axios.post(
        `${API}/trades/proposals`,
        {
          to_coach: toCoach,
          offered_swimmer: offeredSwimmer,
          requested_swimmer: requestedSwimmer,
          note: tradeNote,
        },
        {
          headers: { Authorization: `Bearer ${getAccessToken()}` },
        },
      );
      setStatus("Trade proposal submitted.");
      setToCoach("");
      setOfferedSwimmer("");
      setRequestedSwimmer("");
      setTradeNote("");
      fetchState();
    } catch (error) {
      setStatus(error?.response?.data?.detail || "Unable to submit proposal.");
    }
  };

  const proposalAction = async (proposalId, action) => {
    try {
      await axios.post(
        `${API}/trades/proposals/${proposalId}/action`,
        { action },
        {
          headers: { Authorization: `Bearer ${getAccessToken()}` },
        },
      );
      setStatus(`Proposal ${action}ed.`);
      fetchState();
    } catch (error) {
      setStatus(
        error?.response?.data?.detail || `Unable to ${action} proposal.`,
      );
    }
  };

  const adminReviewProposal = async (proposalId, action) => {
    try {
      await axios.post(
        `${API}/admin/trades/proposals/${proposalId}/review`,
        { action },
        {
          headers: { Authorization: `Bearer ${getAccessToken()}` },
        },
      );
      setStatus(`Trade ${action}d.`);
      fetchState();
    } catch (error) {
      setStatus(error?.response?.data?.detail || `Unable to ${action} trade.`);
    }
  };

  if (loading) {
    return <p>Loading trading center...</p>;
  }

  return (
    <div>
      <div style={styles.tradingHeader}>
        <h1 style={{ margin: 0 }}>🔁 Trading Center</h1>
        <div
          style={
            stateData?.locked
              ? { ...styles.tradingBadge, ...styles.tradingClosedBadge }
              : styles.tradingBadge
          }
        >
          {stateData?.locked ? "Trading Closed" : "Trading Open"}
        </div>
      </div>

      <div style={styles.summaryRow}>
        <div style={styles.summaryCard}>
          <strong>{myTeam.length}</strong>
          <span>My Swimmers</span>
        </div>
        <div style={styles.summaryCard}>
          <strong>
            {
              incomingProposals.filter(
                (proposal) => proposal.status === "pending_coach",
              ).length
            }
          </strong>
          <span>Incoming Pending</span>
        </div>
        <div style={styles.summaryCard}>
          <strong>
            {
              outgoingProposals.filter((proposal) =>
                ["pending_coach", "pending_admin"].includes(proposal.status),
              ).length
            }
          </strong>
          <span>Outgoing Active</span>
        </div>
      </div>

      <section style={styles.card}>
        <h2 style={styles.cardTitle}>Propose Trade</h2>
        <div style={styles.formRow}>
          <select
            value={toCoach}
            onChange={(event) => setToCoach(event.target.value)}
            style={styles.input}
          >
            <option value="">Select target coach</option>
            {coaches
              .filter((coach) => coach !== stateData?.my_coach)
              .map((coach) => (
                <option key={coach} value={coach}>
                  {coach}
                </option>
              ))}
          </select>

          <select
            value={offeredSwimmer}
            onChange={(event) => setOfferedSwimmer(event.target.value)}
            style={styles.input}
          >
            <option value="">Your swimmer to offer</option>
            {sortedMyTeam.map((swimmer) => (
              <option key={swimmer} value={swimmer}>
                {swimmer}
              </option>
            ))}
          </select>

          <select
            value={requestedSwimmer}
            onChange={(event) => setRequestedSwimmer(event.target.value)}
            style={styles.input}
          >
            <option value="">Requested swimmer</option>
            {sortedTargetTeam.map((swimmer) => (
              <option key={swimmer} value={swimmer}>
                {swimmer}
              </option>
            ))}
          </select>

          <input
            value={tradeNote}
            onChange={(event) => setTradeNote(event.target.value)}
            placeholder="Optional note"
            style={styles.input}
          />

          <button
            type="button"
            onClick={submitProposal}
            style={styles.primaryBtn}
          >
            Send Proposal
          </button>
        </div>
      </section>

      <div style={styles.grid}>
        <section style={styles.card}>
          <h2 style={styles.cardTitle}>Incoming Proposals</h2>
          <ProposalList
            proposals={incomingProposals}
            myCoach={stateData?.my_coach}
            onAccept={(id) => proposalAction(id, "accept")}
            onReject={(id) => proposalAction(id, "reject")}
          />
        </section>

        <section style={styles.card}>
          <h2 style={styles.cardTitle}>Outgoing Proposals</h2>
          <ProposalList
            proposals={outgoingProposals}
            myCoach={stateData?.my_coach}
            onCancel={(id) => proposalAction(id, "cancel")}
          />
        </section>
      </div>

      {user?.isAdmin && (
        <section style={styles.card}>
          <h2 style={styles.cardTitle}>Admin Trade Review Queue</h2>
          {adminProposalQueue.length === 0 ? (
            <p style={styles.empty}>No trades awaiting admin approval.</p>
          ) : (
            <div style={styles.list}>
              {adminProposalQueue.map((proposal) => (
                <div key={proposal.id} style={styles.item}>
                  <div style={styles.itemTitle}>
                    {proposal.from_coach}: {proposal.offered_swimmer} ⇄{" "}
                    {proposal.to_coach}: {proposal.requested_swimmer}
                  </div>
                  <div style={styles.actions}>
                    <button
                      type="button"
                      style={styles.approveBtn}
                      onClick={() =>
                        adminReviewProposal(proposal.id, "approve")
                      }
                    >
                      Approve
                    </button>
                    <button
                      type="button"
                      style={styles.denyBtn}
                      onClick={() => adminReviewProposal(proposal.id, "deny")}
                    >
                      Deny
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      {status && (
        <p
          style={
            status.startsWith("Unable") || status.startsWith("You must")
              ? styles.error
              : styles.success
          }
        >
          {status}
        </p>
      )}
    </div>
  );
};

const ProposalList = ({ proposals, myCoach, onAccept, onReject, onCancel }) => {
  if (!proposals || proposals.length === 0) {
    return <p style={styles.empty}>No proposals yet.</p>;
  }

  return (
    <div style={styles.list}>
      {proposals.map((proposal) => {
        const isIncoming = proposal.to_coach === myCoach;
        const canAcceptReject =
          isIncoming && proposal.status === "pending_coach";
        const canCancel =
          !isIncoming &&
          ["pending_coach", "pending_admin"].includes(proposal.status);

        return (
          <div key={proposal.id} style={styles.item}>
            <div style={styles.itemTitle}>
              {proposal.from_coach}: {proposal.offered_swimmer} ⇄{" "}
              {proposal.to_coach}: {proposal.requested_swimmer}
            </div>
            <div style={styles.meta}>Status: {proposal.status}</div>
            {proposal.note && (
              <div style={styles.meta}>Note: {proposal.note}</div>
            )}
            <div style={styles.actions}>
              {canAcceptReject && (
                <>
                  <button
                    type="button"
                    style={styles.approveBtn}
                    onClick={() => onAccept?.(proposal.id)}
                  >
                    Accept
                  </button>
                  <button
                    type="button"
                    style={styles.denyBtn}
                    onClick={() => onReject?.(proposal.id)}
                  >
                    Reject
                  </button>
                </>
              )}
              {canCancel && (
                <button
                  type="button"
                  style={styles.cancelBtn}
                  onClick={() => onCancel?.(proposal.id)}
                >
                  Cancel
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const styles = {
  tradingHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "0.75rem",
    flexWrap: "wrap",
    marginBottom: "0.75rem",
  },
  tradingBadge: {
    padding: "0.45rem 0.7rem",
    borderRadius: "999px",
    border: "1px solid rgba(0,245,212,0.55)",
    backgroundColor: "rgba(0,245,212,0.12)",
    color: "#a8fff0",
    fontWeight: 800,
    fontSize: "0.8rem",
    textTransform: "uppercase",
    letterSpacing: "0.04em",
  },
  tradingClosedBadge: {
    borderColor: "rgba(255, 126, 160, 0.65)",
    backgroundColor: "rgba(255, 126, 160, 0.14)",
    color: "#ffc1d3",
  },
  summaryRow: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
    gap: "0.75rem",
    marginBottom: "1rem",
  },
  summaryCard: {
    border: "1px solid rgba(0,245,212,0.34)",
    borderRadius: "10px",
    backgroundColor: "rgba(7, 20, 60, 0.86)",
    padding: "0.75rem",
    display: "flex",
    flexDirection: "column",
    gap: "0.2rem",
    color: "#eaf3ff",
    boxShadow: "inset 0 0 0 1px rgba(0,245,212,0.08)",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "0.9rem",
    marginTop: "0.75rem",
    marginBottom: "0.9rem",
  },
  card: {
    border: "1px solid rgba(86, 122, 198, 0.45)",
    borderRadius: "10px",
    backgroundColor: "rgba(7, 20, 60, 0.86)",
    padding: "0.9rem",
  },
  cardTitle: {
    marginTop: 0,
    marginBottom: "0.65rem",
    color: "#eaf3ff",
  },
  formRow: {
    display: "flex",
    flexDirection: "column",
    gap: "0.5rem",
  },
  input: {
    border: "1px solid rgba(98, 132, 212, 0.45)",
    borderRadius: "10px",
    padding: "0.45rem 0.6rem",
    fontSize: "0.9rem",
    backgroundColor: "rgba(5, 17, 51, 0.85)",
    color: "#d8e8ff",
  },
  primaryBtn: {
    alignSelf: "flex-start",
    border: "1px solid rgba(0,245,212,0.7)",
    borderRadius: "10px",
    backgroundColor: "rgba(0,245,212,0.14)",
    color: "#a8fff0",
    padding: "0.45rem 0.8rem",
    fontWeight: "bold",
    cursor: "pointer",
  },
  list: {
    display: "flex",
    flexDirection: "column",
    gap: "0.5rem",
  },
  item: {
    border: "1px solid rgba(0,245,212,0.3)",
    borderRadius: "8px",
    padding: "0.6rem",
    backgroundColor: "rgba(9, 23, 64, 0.72)",
    boxShadow: "inset 0 0 0 1px rgba(0,245,212,0.06)",
  },
  itemTitle: {
    fontWeight: "bold",
    color: "#f4f8ff",
    marginBottom: "0.2rem",
  },
  meta: {
    color: "#9fb7e3",
    fontSize: "0.84rem",
  },
  actions: {
    marginTop: "0.45rem",
    display: "flex",
    gap: "0.45rem",
  },
  approveBtn: {
    border: "1px solid rgba(0,245,212,0.7)",
    borderRadius: "8px",
    backgroundColor: "rgba(0,245,212,0.12)",
    color: "#a8fff0",
    padding: "0.35rem 0.65rem",
    fontWeight: "bold",
    cursor: "pointer",
  },
  denyBtn: {
    border: "1px solid rgba(255, 126, 160, 0.65)",
    borderRadius: "8px",
    backgroundColor: "rgba(255, 126, 160, 0.14)",
    color: "#ffc1d3",
    padding: "0.35rem 0.65rem",
    fontWeight: "bold",
    cursor: "pointer",
  },
  cancelBtn: {
    border: "1px solid rgba(98, 132, 212, 0.55)",
    borderRadius: "8px",
    backgroundColor: "rgba(5, 17, 51, 0.85)",
    color: "#a6bce6",
    padding: "0.35rem 0.65rem",
    fontWeight: "bold",
    cursor: "pointer",
  },
  empty: {
    color: "#9fb7e3",
    fontSize: "0.9rem",
    margin: 0,
  },
  success: {
    color: "#a8fff0",
    fontWeight: "bold",
    marginTop: "0.5rem",
  },
  error: {
    color: "#ff93b0",
    fontWeight: "bold",
    marginTop: "0.5rem",
  },
};

export default TradingView;
